import type { CartesianPose } from '@sb/geometry/CartesianPose';
import { makeNamespacedLog } from '@sb/log';
import {
  DEFAULT_WELD_TOOLTIP_ACCELERATION,
  type ArmTarget,
  type PoseArmTarget,
  type TCPFrame,
  type TCPOffsetOption,
} from '@sb/motion-planning';
import type {
  ArcStep,
  EquipmentStateItem,
  ExecutionConfig,
  RoutineContext,
  StepFailure,
  RoutineRunningPauseDetails,
} from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import type {
  StepAction,
  StepPlayArguments,
} from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import type WaypointStep from '@sb/routine-runner/Step/Waypoint/Step';
import { getTCPLinkAndQuaternion } from '@sb/routine-runner/util/tcpFrame';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { wait } from '@sb/utilities';
import type { NonEmptyArray } from '@sb/utilities/src/types';

import { WeldArguments } from './Arguments';
import { WeldVariables } from './Variables';

const log = makeNamespacedLog('WeldStep');

type ResumeKind = 'resume' | 'stop';

/**
 * The relative-to-current integrator state carried across weld target
 * computations: the predecessor pose and the TCP frame it is genuinely
 * expressed in (`undefined` = frame unknown, e.g. a legacy joint-match teach
 * snapshot that stored no tcpOption — a relative read fails loud on it
 * rather than guessing). SUP-4006.
 */
type WeldIntegratorCarry = {
  pose: CartesianPose;
  tcpFrame: TCPFrame | undefined;
};

export class WeldStep extends Step<WeldArguments, WeldVariables> {
  public static areSubstepsRequired = true;

  public static Arguments = WeldArguments;

  public static Variables = WeldVariables;

  public substeps: Array<WaypointStep | ArcStep> = [];

  protected initializeVariableState(): void {
    const { completedWeldCount = 0 } = this.variablesForInitialization;

    this.variables = {
      completedWeldCount,
      currentActivity: 'none',
      approachMovementActivity: 'none',
      weldMovementActivity: 'none',
      weldOnResume: false,
    };
  }

  private motionApproach?: ReturnType<RoutineContext['doMotion']>;

  private motionWeld?: ReturnType<RoutineContext['doMotion']>;

  // Tracks any in-flight _pause work so _resume can await completion before
  // emitting 'resume'. Prevents the emit-order inversion race that caused
  // SUP-3787: _pause awaits stopWeldMachine before emitting 'pause'; if
  // _resume ran in the meantime and emitted 'resume' synchronously, the
  // doMotion lifecycleChain would serialize the handlers as resume→pause
  // and leave the motion loop permanently paused.
  private pauseInFlight: Promise<void> = Promise.resolve();

  // Used to wait for resumption of routine after preflight check completion
  private pauseGate: {
    promise: Promise<ResumeKind>;
    resolve: (kind: ResumeKind) => void;
  } | null = null;

  private getStitchedExecutionConfig(
    weldMachine: EquipmentStateItem,
    fail: (failure: StepFailure) => void,
  ): ExecutionConfig {
    return {
      individualExecution: true,
      beforeExecutionIteration: async (iteration: number) => {
        const isStitchSegment = iteration % 2 === 0;

        if (isStitchSegment) {
          this.setVariable('currentActivity', 'startArc');
          await this.startWeldMachine({ weldMachine, fail });
          await wait(this.args.arcStartTime);
        }
      },
      afterExecutionIteration: async (iteration: number, success: boolean) => {
        const isStitchSegment = iteration % 2 === 0;

        if (isStitchSegment) {
          if (success) {
            // only fill crater if movement was successful
            this.setVariable('currentActivity', 'craterFilling');
            await wait(this.args.craterFillTime);
          }

          this.setVariable('currentActivity', 'stopArc');
          await this.stopWeldMachine({ weldMachine, fail });
        }
      },
    };
  }

  private getContinuousExecutionConfig(
    weldMachine: EquipmentStateItem,
    fail: (failure: StepFailure) => void,
  ): ExecutionConfig {
    return {
      individualExecution: false,
      beforeExecutionIteration: async () => {
        this.setVariable('currentActivity', 'startArc');
        await this.startWeldMachine({ weldMachine, fail });
        await wait(this.args.arcStartTime);
      },
      afterExecutionIteration: async (_iteration: number, success: boolean) => {
        if (success) {
          // only fill crater if movement was successful
          this.setVariable('currentActivity', 'craterFilling');
          await wait(this.args.craterFillTime);
        }

        this.setVariable('currentActivity', 'stopArc');
        await this.stopWeldMachine({ weldMachine, fail });
      },
    };
  }

  public hasSelfTimeout(): boolean {
    return false;
  }

  public async _play(playArgs: StepPlayArguments): Promise<void> {
    // A step instance is replayed (a loop iteration, a play after a stop), so
    // clear the previous play's record before this one pins anything: the
    // restore below must key on what THIS play wrote.
    this.pinnedTCPOption = undefined;

    // The step pins the global motion frame to the torch for its target math
    // (see setRoutineContextTCPOffsetOption). The pin is scoped to this step:
    // the operator's frame is captured here and put back on exit, so a later
    // step that resolves `'auto'` against the register (a relative-to-current
    // waypoint) reads the operator's frame rather than inheriting the torch.
    const preStepTCPOption =
      this.routineContext.getRoutineRunnerState().kinematicState
        .tcpOffsetOption;

    try {
      await this.playWeld(playArgs);
    } finally {
      this.restorePreStepTCPOption(preStepTCPOption);
    }
  }

  /**
   * Put the global motion frame back where it was before this step's torch
   * pin. Restores only this step's own write: a register moved by someone
   * else mid-play (an operator jog under pause, a WebSocket
   * `setTCPOffsetOption`) keeps their choice, and a play that failed before
   * pinning (`pinnedTCPOption` unset) restores nothing.
   */
  private restorePreStepTCPOption(preStepTCPOption: TCPOffsetOption): void {
    const { tcpOffsetOption } =
      this.routineContext.getRoutineRunnerState().kinematicState;

    if (
      this.pinnedTCPOption != null &&
      tcpOffsetOption === this.pinnedTCPOption &&
      tcpOffsetOption !== preStepTCPOption
    ) {
      this.routineContext.setTCPOffsetOption(preStepTCPOption);
    }
  }

  private async playWeld({
    fail,
    hasEncounteredStartStep,
    pauseRoutine,
  }: StepPlayArguments): Promise<void> {
    if (!hasEncounteredStartStep()) {
      // Skip weld steps if we haven't reached the start step
      log.info(
        '_play',
        "Skipping Weld step because we haven't reached the start step yet",
      );

      return;
    }

    const { weldMachine, torch } = this.activeEquipment;

    if (weldMachine == null) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'Weld machine not found',
      });

      return;
    }

    if (torch == null) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'Weld torch not found',
      });

      return;
    }

    this.setRoutineContextTCPOffsetOption();

    const activeSubsteps = this.getActiveSubsteps();

    if (activeSubsteps.length === 0) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'No waypoints found',
      });

      return;
    }

    // Read the matched read/write-frame flag ONCE at step start: the
    // approach-boundary bridge below is gated on it, so OFF stays
    // byte-identical to today.
    const matchFramesEnabled = getFeatureFlagSync(
      'matchRelativeToCurrentFrames',
      true,
    );

    // The approach's FIRST-evaluation integrator carry (pose + frame tag).
    // The body computations chain from this — never from a recomputation of
    // the approach: recomputing a relative-to-current approach from the
    // post-move live state re-applies its offset (start + 2·offset), and that
    // contaminated pose became the body carry (a zero-offset relative body
    // waypoint then moved by the approach offset again). SUP-4006.
    let approachCarry: WeldIntegratorCarry | undefined;

    // Get the approach target and return early if it fails
    const [approachTarget] = (await this.getArmTargets({
      substeps: [activeSubsteps[0]],
      fail,
      // Don't convert to pose arm target for approach target because user may have set shouldMatchJointAngles to true
      convertToPose: false,
      onCarryOut: (carry) => {
        approachCarry = carry;
      },
    })) ?? [undefined];

    if (approachTarget === undefined) {
      return;
    }

    this.setVariable('currentActivity', 'approaching');

    const approachCompleted = await this.moveToPosition({
      fail,
      armTarget: approachTarget,
    });

    if (!approachCompleted) {
      log.warn('_play', 'Approach movement failed. Returning.', {
        variables: this.variables,
      });

      return;
    }

    // Handle preflight test run completion when approach is complete
    const shouldContinue = await this.handlePreflightPause(pauseRoutine);

    if (!shouldContinue) {
      log.info('_play', 'Preflight check not completed. Returning.');

      return;
    }

    this.setVariable('currentActivity', 'settingWeldParameters');
    await this.setWeldMachineParameters({ weldMachine, fail });

    log.info('weldingParameters', 'parameters', {
      weldParameters: this.args,
    });

    // Bridge the approach boundary only on the matched-frames flag; OFF, the
    // body computations keep today's recompute-and-discard path verbatim.
    const bridgedApproachCarry = matchFramesEnabled ? approachCarry : undefined;

    if (this.args.weaving.enabled) {
      await this.performWeavingWeld({
        weldMachine,
        fail,
        approachCarry: bridgedApproachCarry,
      });
    } else if (this.args.stitching.enabled) {
      await this.performStitchedWeld({
        weldMachine,
        fail,
        approachCarry: bridgedApproachCarry,
      });
    } else {
      await this.performContinuousWeld({
        weldMachine,
        fail,
        approachCarry: bridgedApproachCarry,
      });
    }

    this.setVariable('currentActivity', 'none');

    this.setVariable(
      'completedWeldCount',
      this.variables.completedWeldCount + 1,
    );
  }

  /**
   * The body targets for the weld pass — every active substep after the
   * approach, as pose targets.
   *
   * With an `approachCarry` (matched-frames flag ON), computes ONLY the body
   * substeps, chaining the integrator from the approach's first evaluation.
   * Without one (flag OFF), recomputes ALL substeps from the live robot state
   * and discards the recomputed approach — today's path, kept verbatim for a
   * byte-identical flag-OFF: for a relative-to-current approach that
   * recomputation re-applies the offset from the post-move pose, so the
   * discarded target's pose contaminates the body carry (SUP-4006).
   */
  private async getBodyTargets({
    fail,
    approachCarry,
  }: {
    fail: (failure: StepFailure) => void;
    approachCarry?: WeldIntegratorCarry;
  }): Promise<Array<ArmTarget & PoseArmTarget> | undefined> {
    if (approachCarry) {
      return this.getArmTargets({
        substeps: this.getActiveSubsteps().slice(1),
        fail,
        convertToPose: true,
        seed: approachCarry,
      });
    }

    const allTargets = await this.getArmTargets({
      substeps: this.getActiveSubsteps(),
      fail,
      convertToPose: true,
    });

    // Skip the approach target
    return allTargets?.slice(1);
  }

  /**
   * Perform a continuous weld without stitching
   */
  private async performContinuousWeld({
    weldMachine,
    fail,
    approachCarry,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
    approachCarry?: WeldIntegratorCarry;
  }): Promise<void> {
    const targets = await this.getBodyTargets({ fail, approachCarry });

    if (targets === undefined) {
      log.error('performContinuousWeld', 'No targets found. Returning.');

      return;
    }

    await this.moveDuringWeld({
      targets,
      fail,
      useCartesianPlanner: true,
      executionConfig: this.getContinuousExecutionConfig(weldMachine, fail),
    });
  }

  /**
   * Perform a stitched weld, alternating between welding and not welding
   */
  private async performStitchedWeld({
    weldMachine,
    fail,
    approachCarry,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
    approachCarry?: WeldIntegratorCarry;
  }): Promise<void> {
    const targets = await this.getBodyTargets({ fail, approachCarry });

    if (targets === undefined) {
      log.error('performStitchedWeld', 'No targets found. Returning.');

      return;
    }

    // Track whether the stitch pattern *logically* expects welding to be on.
    // This is persisted so pause/resume logic can make correct decisions even
    // when we're currently in a "space" (non‑welding) segment.
    // let isWelding = false;

    await this.moveDuringWeld({
      targets,
      fail,
      shouldResample: false,
      useCartesianPlanner: true,
      executionConfig: this.getStitchedExecutionConfig(weldMachine, fail),
    });
  }

  private async performWeavingWeld({
    weldMachine,
    fail,
    approachCarry,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
    approachCarry?: WeldIntegratorCarry;
  }): Promise<void> {
    const targets = await this.getBodyTargets({ fail, approachCarry });

    if (targets === undefined) {
      log.error('performWeavingWeld', 'No targets found. Returning.');

      return;
    }

    // Use base path targets only; the ROS2 backend will generate the weave pattern
    await this.moveDuringWeld({
      targets,
      fail,
      shouldResample: false,
      useCartesianPlanner: true,
      executionConfig: this.getContinuousExecutionConfig(weldMachine, fail),
    });
  }

  public async _stop({ fail }: StepAction): Promise<void> {
    log.info('_stop', 'Stopping Weld step');

    this.resolvePauseGate('stop'); // ensure no pending waits

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_stop', 'No weld machine found. Nothing to stop.');

      return;
    }

    this.setVariable('currentActivity', 'stopArc');
    await this.stopWeldMachine({ weldMachine, fail });

    if (this.motionApproach != null) {
      this.motionApproach.emit('cancel');
    }

    if (this.motionWeld != null) {
      this.motionWeld.emit('cancel');
    }

    this.setVariable('currentActivity', 'none');
  }

  public async _pause({ fail }: StepAction): Promise<void> {
    log.info('_pause', 'Pausing Weld step');

    this.pauseInFlight = (async () => {
      const { weldMachine } = this.activeEquipment;

      if (weldMachine == null) {
        log.info('_pause', 'No weld machine found. Nothing to pause.');

        return;
      }

      await this.stopWeldMachine({ weldMachine, fail, keepState: true });

      if (this.motionApproach != null) {
        this.motionApproach.emit('pause');
      }

      if (this.motionWeld != null) {
        this.motionWeld.emit('pause');
      }
    })();

    await this.pauseInFlight;
  }

  public async _resume({ fail }: StepAction): Promise<void> {
    log.info('_resume', 'Resuming Weld step');

    // Wait for any in-flight _pause to finish emitting 'pause' on the
    // motion emitters before we emit 'resume'. Without this, a _resume
    // triggered while _pause is still awaiting stopWeldMachine would land
    // its emit first and the doMotion lifecycleChain would leave the loop
    // stuck in the paused branch. See SUP-3787.
    await this.pauseInFlight;

    this.resolvePauseGate('resume'); // unblock _play() waiting for resume

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_resume', 'No weld machine found. Nothing to resume.');

      return;
    }

    // Main question: do we need to be starting weld machine again once approach is complete?
    if (this.motionApproach != null) {
      this.motionApproach.emit('resume');
      this.setVariable('approachMovementActivity', 'moving');
    }

    if (this.motionWeld != null) {
      // If we were welding when paused
      if (this.variables.weldOnResume) {
        this.setVariable('currentActivity', 'startArc');
        await this.startWeldMachine({ weldMachine, fail });
      }

      this.motionWeld.emit('resume');
      this.setVariable('weldMovementActivity', 'moving');
      this.setVariable('currentActivity', 'movingDuringWeld');
    }
  }

  private get activeEquipment(): {
    weldMachine: EquipmentStateItem | undefined;
    torch: EquipmentStateItem | undefined;
  } {
    const allEquipment = this.routineContext.equipment.getEquipmentState();

    const weldMachine = allEquipment.find(
      (equipment) => equipment.id === this.args.selectedMachineID,
    );

    const torch = allEquipment.find(
      (equipment) => equipment.id === this.args.selectedTorchID,
    );

    return { weldMachine, torch };
  }

  private getTCPOffsetOption(): TCPOffsetOption {
    const { torch } = this.activeEquipment;

    if (torch == null) {
      throw new Error('Torch not found');
    }

    if (this.args.selectedTorchID == null) {
      throw new Error('Selected torch ID is null');
    }

    // TCP Offset option a bit of a hack. May need to revisit the indexing once we add dedicated torches, not just custom grippers.
    // See https://github.com/standardbots/sb/blob/540d718e5d2bbe3226ea9b4128b9f49392bfc6ef/libs/integrations/implementations/CustomGripper/frontend.tsx#L30
    return `ee-${torch.state.kind}-0`;
  }

  /**
   * The torch option this play's pin wrote to the register, recorded so the
   * end-of-play restore can tell its own write from anyone else's.
   */
  private pinnedTCPOption?: TCPOffsetOption;

  private setRoutineContextTCPOffsetOption(): void {
    const tcpOffset = this.getTCPOffsetOption();
    this.pinnedTCPOption = tcpOffset;
    this.routineContext.setTCPOffsetOption(tcpOffset);
  }

  private async moveToPosition({
    armTarget,
    fail,
  }: {
    armTarget: ArmTarget;
    fail: (failure: StepFailure) => void;
    useCartesianPlanner?: boolean;
  }): Promise<boolean> {
    // Weld does not observe push mode: should not be pushing up against object.
    // Also, ros2 does not support push at this time (2025-02-19).
    let completed = false;

    const motion = this.routineContext.doMotion({
      request: {
        targets: [armTarget] as NonEmptyArray<ArmTarget>,
      },
      speedProfiles: [this.args.approachSpeedProfile],
      pushUntilCollision: false,
      pushMode: false,
      stepID: this.id,
    });

    motion.on('requestingPlan', () => {
      // Only checking previous state for first few states because we could be in a play/pause/resume flow.
      if (this.variables.approachMovementActivity === 'none') {
        this.setVariable('approachMovementActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.approachMovementActivity === 'requestingPlan') {
        this.setVariable('approachMovementActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('approachMovementActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('approachMovementActivity', 'paused');
    });

    motion.on('complete', () => {
      log.debug('moveToApproachPosition', 'Approach motion complete');

      completed = true;
    });

    motion.on('cancelled', () => {
      this.setVariable('approachMovementActivity', 'none');
    });

    motion.on('failure', (failure) => {
      this.setVariable('approachMovementActivity', 'none');
      fail(failure);
    });

    this.motionApproach = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      this.setVariable('approachMovementActivity', 'none');
      motion.removeAllListeners();

      delete this.motionApproach;
    }

    return completed;
  }

  /**
   * Handles movement one set of targets at a time.
   */
  private async moveDuringWeld({
    targets,
    fail,
    shouldResample = false,
    useCartesianPlanner = false,
    executionConfig,
  }: {
    targets: ArmTarget[];
    fail: (failure: StepFailure) => void;
    shouldResample?: boolean;
    useCartesianPlanner?: boolean;
    executionConfig?: ExecutionConfig;
  }): Promise<boolean> {
    let completed = false;

    if (targets.length === 0) {
      log.debug('moveDuringWeld', 'No targets found. Returning.');

      return completed;
    }

    // Get effective TCP option from selected torch
    const effectiveTCPOption = this.getTCPOffsetOption();

    log.info('moveDuringWeld', `Using torch TCP option:`, {
      effectiveTCPOption,
      selectedTorchID: this.args.selectedTorchID,
    });

    // Convert tcpOption to ROS link name for cartesian planning
    const { equipmentItems } = this.routineContext.equipment;

    const tcpResult = getTCPLinkAndQuaternion(
      equipmentItems,
      effectiveTCPOption,
    );

    const toolFrame = tcpResult?.tcpLink ?? 'tool0'; // Fallback to flange

    log.info(
      'moveDuringWeld',
      `Tooltip selection: tcpOption='${effectiveTCPOption}' → toolFrame='${toolFrame}'`,
    );

    // Weld does not observe push mode: should not be pushing up against object.
    // Also, ros2 does not support push at this time (2025-02-19).
    const motion = this.routineContext.doMotion({
      request: {
        targets: targets as NonEmptyArray<ArmTarget>,
        useCartesianPlanner,
        toolFrame, // Add tool frame selection
        weaveParameters: this.args.weaving,
        stitchParameters: {
          ...this.args.stitching,
          spaceTravelSpeed:
            this.args.stitching.spaceSpeedMultiplier * this.args.travelSpeed,
          spaceTravelAcceleration:
            // use max tooltip acceleration from weld travel speed profile
            this.args.weldTravelSpeedProfile.maxTooltipAcceleration ??
            DEFAULT_WELD_TOOLTIP_ACCELERATION,
        },
      },
      speedProfiles: targets.map(() => this.args.weldTravelSpeedProfile),
      pushUntilCollision: false,
      pushMode: false,
      stepID: this.id,
      shouldResample,
      executionConfig,
    });

    motion.on('requestingPlan', () => {
      if (this.variables.weldMovementActivity === 'none') {
        this.setVariable('weldMovementActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.weldMovementActivity === 'requestingPlan') {
        this.setVariable('weldMovementActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('weldMovementActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('weldMovementActivity', 'paused');
    });

    motion.on('complete', () => {
      log.debug('moveDuringWeld', 'Welding motion complete');

      completed = true;
    });

    motion.on('cancelled', () => {
      this.setVariable('weldMovementActivity', 'none');
    });

    motion.on('failure', (failure) => {
      this.setVariable('weldMovementActivity', 'none');
      fail(failure);
    });

    this.motionWeld = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      this.setVariable('weldMovementActivity', 'none');
      motion.removeAllListeners();

      delete this.motionWeld;
    }

    return completed;
  }

  /**
   * Stop the current weld machine.
   *
   * Weld stop commands may be sent even if welding hasn't started yet.
   */
  private async stopWeldMachine({
    weldMachine,
    fail,
    keepState = false,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
    keepState?: boolean;
  }): Promise<void> {
    let failureReason: string;

    if (!keepState) {
      this.setVariable('weldOnResume', false);
    }

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        await this.stopMillerWeldMachine({ fail });
        this.setVisualizerWeldArcFlashEffect(false);

        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;

      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async startWeldMachine({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    let failureReason: string;

    this.setVariable('weldOnResume', true);

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        try {
          await this.startMillerWeldMachine({ fail });
        } catch (error) {
          fail({
            failure: { kind: FailureKind.WeldFailure },
            failureReason: `Failed to start weld machine: ${error.message}`,
            error,
          });
        }

        this.setVisualizerWeldArcFlashEffect(true);
        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async setWeldMachineParameters({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    let failureReason: string;

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        await this.setMillerWeldParameters({ fail });
        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async stopMillerWeldMachine({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'stopArc',
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to stop weld machine: ${error.message}`,
        error,
      });
    }
  }

  private async startMillerWeldMachine({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'startArc',
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to start weld machine: ${error.message}`,
        error,
      });
    }
  }

  private async setMillerWeldParameters({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'setWeldParameters',
          weldTravelSpeed: 0, // This is disregarded by machine. Just set it like this for now.
          ...this.args.millerWeldParameters,
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to set weld machine parameters: ${error.message}`,
        error,
      });
    }
  }

  /**
   * Returns unsuppressed substeps only. Suppressed waypoints/arcs are excluded
   * so the robot does not move through or weld them — WeldStep calls
   * getArmTarget() directly rather than routing through RoutineRunner.play()'s
   * suppression check, so filtering must happen here.
   */
  private getActiveSubsteps(): Array<WaypointStep | ArcStep> {
    return this.substeps.filter((s) => !s.isEffectivelySuppressed());
  }

  /**
   * Get the arm targets for the given substeps.
   *
   * Optionally converts joint arm targets to pose arm targets.
   *
   * Fancy typing ensures the output array length matches the input array length, and enforces
   * the conversion to pose arm targets if requested.
   */
  private async getArmTargets<
    T extends Array<WaypointStep | ArcStep>,
    P extends boolean = true,
  >({
    substeps,
    fail,
    convertToPose = true as P,
    seed,
    onCarryOut,
  }: {
    substeps: T;
    fail: (failure: StepFailure) => void;
    convertToPose?: P;
    /**
     * Integrator start for a body computation that chains from the approach's
     * first evaluation (pose + the TCP frame it is genuinely expressed in)
     * instead of the live robot state. Consumed only on the matched-frames
     * flag (SUP-4006).
     */
    seed?: WeldIntegratorCarry;
    /**
     * Receives the final integrator carry of this computation — `_play` uses
     * it to bridge the approach's first evaluation into the body computation.
     * Called only on success.
     */
    onCarryOut?: (carry: WeldIntegratorCarry) => void;
  }): Promise<
    | { [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget }
    | undefined
  > {
    const targets: ArmTarget[] = [];

    const { kinematicState } = this.routineContext.getRoutineRunnerState();

    let previousTargetPose: CartesianPose =
      kinematicState.tcpOffsetOption === 'wrist'
        ? kinematicState.wristPose
        : kinematicState.tooltipPoint;

    // The TCP frame `previousTargetPose` is genuinely expressed in. Threaded
    // flag-ON only — the flag-OFF path carries no tag, exactly today's
    // behavior.
    let previousTargetTCPFrame: TCPFrame | undefined;

    // Read once per computation: it gates the seed, the substep param shape,
    // and the tag threading together, so they can never disagree mid-loop.
    const matchFramesEnabled = getFeatureFlagSync(
      'matchRelativeToCurrentFrames',
      true,
    );

    if (matchFramesEnabled) {
      if (seed) {
        // Bridged body computation: chain from the approach's first
        // evaluation rather than re-deriving from the post-move live state —
        // recomputing a relative-to-current approach from the moved pose
        // re-applies its offset, and that pose contaminated the body carry.
        previousTargetPose = seed.pose;
        previousTargetTCPFrame = seed.tcpFrame;
      } else {
        // SUP-4006: seed relative-to-current waypoints in the torch TCP,
        // derived from the flange pose (frame-independent) — `tooltipPoint`
        // is not recomputed when the active TCP changes, so the seed above
        // can still be in the pre-weld frame. Without this re-expression, the
        // move puts the flange at the offset point instead of the torch TCP:
        // a nosedive by the tool-offset difference.
        previousTargetPose = this.routineContext.reexpressTooltipPose(
          kinematicState.wristPose,
          { tcpOption: 'wrist' },
          { tcpOption: this.getTCPOffsetOption() },
        );

        // Live-state seed, freshly expressed in the torch TCP. No gripperID:
        // the torch resolves by name against enabled equipment — the same
        // resolution command consumption uses for these targets.
        previousTargetTCPFrame = { tcpOption: this.getTCPOffsetOption() };
      }
    }

    for (let i = 0; i < substeps.length; i += 1) {
      const substep: WaypointStep | ArcStep = substeps[i];

      if (
        substep.getStepKind() !== 'Waypoint' &&
        substep.getStepKind() !== 'Arc'
      ) {
        // This is a dev error. Will need to update once we add additional flourishes.
        throw new Error(
          `Substep is not a waypoint or arc: kind=${substep.getStepKind()}`,
        );
      }

      // Flag-OFF: today's substep call, byte-identical for Waypoint and Arc.
      // Flag-ON: substep-kind-specific plumbing — see
      // `getMatchedFramesSubstepTarget`.
      const result = await (matchFramesEnabled
        ? this.getMatchedFramesSubstepTarget({
            substep,
            previousTargetPose,
            previousTargetTCPFrame,
          })
        : substep.getArmTarget({
            effectiveTCPOption: this.getTCPOffsetOption(),
            parentCompletedCount: this.variables.completedWeldCount,
            previousTargetPose,
          }));

      if ('failure' in result) {
        fail(result);

        return undefined;
      }

      const maybeTargetArray = result.target;
      const { poseForPreviousTarget } = result;

      previousTargetPose = poseForPreviousTarget;

      // Carry the tag in lock-step with `previousTargetPose`. It MUST name
      // the frame the carry pose is GENUINELY in — not always the torch: a
      // joint-match substep carries its stored teach-time pose in its stored
      // frame, and reading that carry as the torch is a dive by the
      // tool-offset gap.
      if (matchFramesEnabled) {
        previousTargetTCPFrame = result.poseForPreviousTargetTCPFrame;
      }

      const targetArray = Array.isArray(maybeTargetArray)
        ? maybeTargetArray
        : [maybeTargetArray];

      for (let target of targetArray) {
        // Only convert to pose if requested
        if (convertToPose && !('pose' in target)) {
          target = {
            ...target,
            // this is a joint arm target so lets convert it to a pose arm target
            pose: await this.routineContext.getTargetPose(target),
          };

          if ('jointAngles' in target) {
            // remove jointAngles from target otherwise motion planner will use them
            delete target.jointAngles;
          }
        }

        targets.push(target);
      }
    }

    onCarryOut?.({
      pose: previousTargetPose,
      tcpFrame: previousTargetTCPFrame,
    });

    return targets as {
      [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget;
    };
  }

  /**
   * The flag-ON (matched relative-to-current frames) substep target call.
   *
   * Waypoint substeps get the relative-to-current frame plumbing (mirrors
   * MoveArmToV2 under constant speed): `forcedTCPOption` pins both the
   * read/write frame and the commanded frame to the torch — stamped inside
   * getArmTarget BEFORE the carry tag is computed, so the tag stays truthful
   * (a joint-match substep keeps its stored teach frame).
   * `capturedGlobalTCPOption` is the weld step's captured global: `_play`
   * forces the global TCP to the torch at step start.
   *
   * Arc substeps keep the forced `effectiveTCPOption` (an arc has no
   * relative-to-current read) and additionally receive the carry tag:
   * `maintainStartOrientation` copies the carried ORIENTATION onto the arc's
   * commanded targets and carry-out, so when the carry is genuinely in a
   * different frame (a joint-replay approach taught with another tool) the
   * arc re-expresses it into its commanded frame instead of commanding the
   * teach-frame quaternion under a torch-frame carry tag (SUP-4006).
   */
  private getMatchedFramesSubstepTarget({
    substep,
    previousTargetPose,
    previousTargetTCPFrame,
  }: {
    substep: WaypointStep | ArcStep;
    previousTargetPose: CartesianPose;
    previousTargetTCPFrame: TCPFrame | undefined;
  }):
    | ReturnType<WaypointStep['getArmTarget']>
    | ReturnType<ArcStep['getArmTarget']> {
    if (this.isWaypointSubstep(substep)) {
      return substep.getArmTarget({
        parentCompletedCount: this.variables.completedWeldCount,
        previousTargetPose,
        capturedGlobalTCPOption: this.getTCPOffsetOption(),
        previousTargetTCPFrame,
        forcedTCPOption: this.getTCPOffsetOption(),
      });
    }

    return substep.getArmTarget({
      effectiveTCPOption: this.getTCPOffsetOption(),
      parentCompletedCount: this.variables.completedWeldCount,
      previousTargetPose,
      previousTargetTCPFrame,
    });
  }

  private isWaypointSubstep(
    substep: WaypointStep | ArcStep,
  ): substep is WaypointStep {
    return substep.getStepKind() === 'Waypoint';
  }

  // Enable/disable the weld flash effect on the torch in visualizer
  private setVisualizerWeldArcFlashEffect(isWelding: boolean) {
    // Get current weld machine state
    const { weldMachine } = this.activeEquipment;

    // TODO: fix the return type of activeEquipment.weldMachine to
    // specify that these are weld machine state items (then we don't need to check for 'isWelding' in the state)
    if (weldMachine && 'isWelding' in weldMachine.state) {
      weldMachine.state.isWelding = isWelding;
    }
  }

  /**
   * Handle preflight test run completion - pause the routine if in preflight mode.
   * When pausing, waits for resume/stop to be called.
   * Returns true if:
   *  - preflight check was completed and routine was resumed, OR
   *  - preflight was not active
   * Returns false if:
   *  - routine was stopped while paused for preflight check completion
   */
  private async handlePreflightPause(
    pauseRoutine: (pauseDetails: RoutineRunningPauseDetails) => Promise<void>,
  ): Promise<boolean> {
    log.info('checkPreflightComplete', 'checking preflight completion');

    const routineRunnerState = this.routineContext.getRoutineRunnerState();

    if (
      routineRunnerState.kind === 'RoutineRunning' &&
      routineRunnerState.isPreflightTestRun
    ) {
      log.info('checkPreflightComplete', 'preflight test run completed');

      // Set isPreflightTestRun to false
      this.routineContext.setRoutineRunnerState({
        ...routineRunnerState,
        isPreflightTestRun: false,
        readyToBeginMotion: true,
        shouldCheckGuidedMode: false,
        shouldNextArmMoveBeGuidedMode: false,
      });

      // Trigger pause for preflight test run completion
      await pauseRoutine({
        kind: 'preflightTestRunCompleted',
        reason: 'The jogging motion completed.',
      });

      const kind = await this.waitForResume();

      if (kind === 'stop') {
        // routine was stopped, so we return false
        return false;
      }

      // routine was resumed, so we return true
      return true;
    }

    log.info('checkPreflightComplete', 'preflight is not active');

    // preflight was not active, so we return true
    return true;
  }

  private createPauseGate(): void {
    if (this.pauseGate) return;

    let resolve!: (kind: ResumeKind) => void;

    const promise = new Promise<ResumeKind>((r) => {
      resolve = r;
    });

    this.pauseGate = { promise, resolve };
  }

  private async waitForResume(): Promise<ResumeKind> {
    this.createPauseGate();

    const kind = await this.pauseGate!.promise;
    this.pauseGate = null;

    return kind;
  }

  private resolvePauseGate(kind: ResumeKind): void {
    if (!this.pauseGate) return;

    this.pauseGate.resolve(kind);
    this.pauseGate = null;
  }
}
